import React, { useState, useEffect } from 'react';
import { Shield, Check, Edit, Plus, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const RolesPermissions = ({ i18n }) => {
  const [roles, setRoles] = useState([
    {
      id: 'super_admin',
      name: 'Super Admin',
      nameAr: 'مدير النظام',
      color: 'from-purple-500 to-pink-500',
      permissions: ['all'],
      description: 'Full system access with all permissions',
      descriptionAr: 'صلاحيات كاملة لإدارة النظام',
      isSystem: true
    },
    {
      id: 'admin',
      name: 'Admin',
      nameAr: 'مدير',
      color: 'from-blue-500 to-cyan-500',
      permissions: ['manage_users', 'manage_content', 'view_analytics', 'manage_support'],
      description: 'Can manage users and content',
      descriptionAr: 'إدارة المستخدمين والمحتوى',
      isSystem: true
    },
    {
      id: 'editor',
      name: 'Editor',
      nameAr: 'محرر',
      color: 'from-emerald-500 to-teal-500',
      permissions: ['create_content', 'edit_content', 'publish_content'],
      description: 'Can create and edit content',
      descriptionAr: 'إنشاء وتحرير المحتوى',
      isSystem: false
    },
    {
      id: 'moderator',
      name: 'Moderator',
      nameAr: 'مشرف',
      color: 'from-amber-500 to-orange-500',
      permissions: ['view_content', 'moderate_content', 'respond_support'],
      description: 'Can moderate and respond to support',
      descriptionAr: 'الإشراف والرد على الدعم',
      isSystem: false
    },
    {
      id: 'user',
      name: 'User',
      nameAr: 'مستخدم',
      color: 'from-slate-400 to-slate-500',
      permissions: ['view_content', 'take_tests', 'track_progress'],
      description: 'Basic user access',
      descriptionAr: 'صلاحيات المستخدم الأساسية',
      isSystem: true
    }
  ]);

  const [editingRole, setEditingRole] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const allPermissions = [
    { id: 'all', name: 'All Permissions', nameAr: 'جميع الصلاحيات' },
    { id: 'manage_users', name: 'Manage Users', nameAr: 'إدارة المستخدمين' },
    { id: 'manage_content', name: 'Manage Content', nameAr: 'إدارة المحتوى' },
    { id: 'create_content', name: 'Create Content', nameAr: 'إنشاء المحتوى' },
    { id: 'edit_content', name: 'Edit Content', nameAr: 'تحرير المحتوى' },
    { id: 'delete_content', name: 'Delete Content', nameAr: 'حذف المحتوى' },
    { id: 'publish_content', name: 'Publish Content', nameAr: 'نشر المحتوى' },
    { id: 'view_content', name: 'View Content', nameAr: 'عرض المحتوى' },
    { id: 'moderate_content', name: 'Moderate Content', nameAr: 'الإشراف على المحتوى' },
    { id: 'view_analytics', name: 'View Analytics', nameAr: 'عرض التحليلات' },
    { id: 'manage_support', name: 'Manage Support', nameAr: 'إدارة الدعم' },
    { id: 'respond_support', name: 'Respond to Support', nameAr: 'الرد على الدعم' },
    { id: 'take_tests', name: 'Take Tests', nameAr: 'إجراء الاختبارات' },
    { id: 'track_progress', name: 'Track Progress', nameAr: 'تتبع التقدم' },
  ];

  const handleEditRole = (role) => {
    setEditingRole({ ...role });
    setModalOpen(true);
  };

  const handleSaveRole = () => {
    if (!editingRole.name.trim()) {
      toast.error(i18n.language === 'ar' ? 'الرجاء إدخال اسم الدور' : 'Please enter role name');
      return;
    }

    if (editingRole.permissions.length === 0) {
      toast.error(i18n.language === 'ar' ? 'الرجاء اختيار صلاحية واحدة على الأقل' : 'Please select at least one permission');
      return;
    }

    if (editingRole.id) {
      // Update existing role
      setRoles(roles.map(r => r.id === editingRole.id ? editingRole : r));
      toast.success(i18n.language === 'ar' ? 'تم تحديث الدور بنجاح! ✅' : 'Role updated successfully! ✅');
    } else {
      // Add new role
      const newRole = {
        ...editingRole,
        id: editingRole.name.toLowerCase().replace(/\s+/g, '_'),
        isSystem: false
      };
      setRoles([...roles, newRole]);
      toast.success(i18n.language === 'ar' ? 'تم إضافة الدور بنجاح! ✅' : 'Role added successfully! ✅');
    }

    setModalOpen(false);
    setEditingRole(null);
  };

  const handleDeleteRole = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (role?.isSystem) {
      toast.error(i18n.language === 'ar' ? 'لا يمكن حذف أدوار النظام' : 'Cannot delete system roles');
      return;
    }

    if (window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من حذف هذا الدور؟' : 'Are you sure you want to delete this role?')) {
      setRoles(roles.filter(r => r.id !== roleId));
      toast.success(i18n.language === 'ar' ? 'تم حذف الدور' : 'Role deleted');
    }
  };

  const togglePermission = (permId) => {
    const perms = editingRole.permissions.includes(permId)
      ? editingRole.permissions.filter(p => p !== permId)
      : [...editingRole.permissions, permId];
    setEditingRole({ ...editingRole, permissions: perms });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-purple-600" />
            {i18n.language === 'ar' ? 'الأدوار والصلاحيات' : 'Roles & Permissions'}
          </h3>
          <Button
            onClick={() => {
              setEditingRole({
                name: '',
                nameAr: '',
                color: 'from-indigo-500 to-purple-500',
                permissions: [],
                description: '',
                descriptionAr: ''
              });
              setModalOpen(true);
            }}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <Plus className="w-4 h-4 me-2" />
            {i18n.language === 'ar' ? 'إضافة دور جديد' : 'Add New Role'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((role) => (
            <div key={role.id} className="bg-white rounded-xl p-6 border-2 border-slate-200 hover:border-slate-300 transition-all">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${role.color} flex items-center justify-center mb-4`}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-lg font-bold text-slate-900">
                  {i18n.language === 'ar' ? (role.nameAr || role.name) : role.name}
                </h4>
                {!role.isSystem && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEditRole(role)}
                      className="p-1 hover:bg-blue-50 rounded text-blue-600"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role.id)}
                      className="p-1 hover:bg-red-50 rounded text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {i18n.language === 'ar' ? (role.descriptionAr || role.description) : role.description}
              </p>
              <div className="space-y-2">
                {role.permissions.slice(0, 4).map((perm) => {
                  const permData = allPermissions.find(p => p.id === perm);
                  return (
                    <div key={perm} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-600" />
                      {i18n.language === 'ar' ? (permData?.nameAr || perm) : (permData?.name || perm)}
                    </div>
                  );
                })}
                {role.permissions.length > 4 && (
                  <div className="text-xs text-slate-500 italic">
                    +{role.permissions.length - 4} {i18n.language === 'ar' ? 'المزيد' : 'more'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit/Add Role Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-purple-600" />
              {editingRole?.id 
                ? (i18n.language === 'ar' ? 'تعديل الدور' : 'Edit Role')
                : (i18n.language === 'ar' ? 'إضافة دور جديد' : 'Add New Role')
              }
            </DialogTitle>
          </DialogHeader>

          {editingRole && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{i18n.language === 'ar' ? 'اسم الدور (EN)' : 'Role Name (EN)'}</Label>
                  <Input
                    value={editingRole.name}
                    onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                    placeholder="Editor"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>{i18n.language === 'ar' ? 'اسم الدور (AR)' : 'Role Name (AR)'}</Label>
                  <Input
                    value={editingRole.nameAr || ''}
                    onChange={(e) => setEditingRole({ ...editingRole, nameAr: e.target.value })}
                    placeholder="محرر"
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{i18n.language === 'ar' ? 'الوصف (EN)' : 'Description (EN)'}</Label>
                  <Input
                    value={editingRole.description || ''}
                    onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                    placeholder="Can edit content"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>{i18n.language === 'ar' ? 'الوصف (AR)' : 'Description (AR)'}</Label>
                  <Input
                    value={editingRole.descriptionAr || ''}
                    onChange={(e) => setEditingRole({ ...editingRole, descriptionAr: e.target.value })}
                    placeholder="يمكنه تحرير المحتوى"
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-3 block">{i18n.language === 'ar' ? 'الصلاحيات' : 'Permissions'}</Label>
                <div className="grid grid-cols-2 gap-3 border border-slate-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {allPermissions.map((perm) => (
                    <div key={perm.id} className="flex items-center gap-2">
                      <Checkbox
                        id={perm.id}
                        checked={editingRole.permissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id)}
                      />
                      <label htmlFor={perm.id} className="text-sm cursor-pointer">
                        {i18n.language === 'ar' ? perm.nameAr : perm.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSaveRole} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600">
              <Save className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissions;